import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import PageHeader from '@/components/shared/PageHeader';
import CTASection from '@/components/home/CTASection';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Briefcase, Users, GraduationCap, Shield, Heart, ArrowRight, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { careers as mockCareers } from '@/data/mock';

const CareersPage = () => {
  const [jobs, setJobs] = useState(mockCareers);
  const [loading, setLoading] = useState(!!process.env.REACT_APP_BACKEND_URL);
  const [usingFallback, setUsingFallback] = useState(!process.env.REACT_APP_BACKEND_URL);
  const [error, setError] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!backendUrl) {
      return;
    }

    const fetchJobs = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/careers/public/list`);
        const liveJobs = Array.isArray(response.data) ? response.data : [];

        if (liveJobs.length > 0) {
          setJobs(liveJobs);
          setUsingFallback(false);
          setError(null);
        } else {
          setJobs(mockCareers);
          setUsingFallback(true);
          setError('No live openings were returned. Showing representative roles.');
        }
      } catch (error) {
        console.error('Failed to fetch jobs:', error);
        setJobs(mockCareers);
        setUsingFallback(true);
        setError('Unable to reach the careers API. Showing sample openings instead.');
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, [backendUrl]);

  const benefits = [
    {
      icon: Briefcase,
      title: 'Meaningful Work',
      description: 'Work on infrastructure projects that shape communities and improve lives.',
    },
    {
      icon: GraduationCap,
      title: 'Professional Development',
      description: 'Continuous learning opportunities, certifications, and career advancement paths.',
    },
    {
      icon: Shield,
      title: 'Safety Culture',
      description: 'Industry-leading safety practices with zero compromise on team wellbeing.',
    },
    {
      icon: Users,
      title: 'Collaborative Environment',
      description: 'Work alongside experienced professionals in a supportive team culture.',
    },
    {
      icon: Heart,
      title: 'Comprehensive Benefits',
      description: 'Competitive compensation, health benefits, and work-life balance.',
    },
    {
      icon: MapPin,
      title: 'Stamford Location',
      description: 'Work from our Connecticut headquarters with regional project opportunities.',
    },
  ];

  return (
    <Layout>
      <PageHeader
        title="Join Our Team"
        subtitle="Build your career with purpose. Shape communities. Advance mobility."
        breadcrumb="Careers"
        backgroundImage="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxjaXZpbCUyMGVuZ2luZWVyaW5nfGVufDB8fHx8MTc2NDE0MzU2OHww&ixlib=rb-4.1.0&q=85"
      />

      {/* Intro Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Careers at Redspire</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
              Where Talent Meets Purpose
            </h2>
            <p className="mt-6 text-lg text-gray-600 leading-relaxed">
              Redspire is always looking for disciplined thinkers, curious builders, and safety-driven professionals. 
              Based in Stamford, Connecticut, our teams work on impactful infrastructure projects that shape communities and expand mobility across the region.
            </p>
            <p className="mt-4 text-lg text-gray-600 leading-relaxed">
              We offer a culture of mentorship, continuous training, clear advancement paths, and long-term stability 
              for professionals who want to build careers with lasting impact in Connecticut and the surrounding areas.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Why Redspire</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold text-gray-900">Benefits of Joining Our Team</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-0 shadow-sm hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-xl bg-red-50 flex items-center justify-center mb-6">
                    <benefit.icon className="w-7 h-7 text-red-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Opportunities</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold text-gray-900">Current Openings</h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              Explore opportunities to join our team and contribute to infrastructure that matters.
            </p>
            {usingFallback && (
              <div className="mt-4 inline-flex items-start gap-3 text-left bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <p className="text-sm">
                  Live job listings are not connected yet, so the openings below are representative examples.
                  Connect the backend or update `src/data/mock.js` to control what appears here.
                </p>
              </div>
            )}
          </div>

          <div className="space-y-4 max-w-4xl mx-auto">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
              </div>
            ) : jobs.length > 0 ? (
              jobs.map((job) => (
              <Card
                key={job.id}
                className="border border-gray-100 hover:border-red-100 hover:shadow-lg transition-all duration-300 group"
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <Link to={`/careers/${job.id}`} className="flex-1">
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900 group-hover:text-red-600 transition-colors">
                          {job.title}
                        </h3>
                        <div className="flex flex-wrap items-center gap-3 mt-2">
                          <span className="flex items-center gap-1 text-gray-500 text-sm">
                            <MapPin className="w-4 h-4" />
                            {job.location}
                          </span>
                          <Badge variant="secondary">{job.type}</Badge>
                          <Badge variant="outline">{job.department}</Badge>
                          {job.postedDate && (
                            <span className="text-xs text-gray-400">
                              Posted {new Date(job.postedDate).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                    <Link to={`/careers/${job.id}/apply`}>
                      <Button className="bg-red-600 hover:bg-red-700 text-white group/btn">
                        Apply Now
                        <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
              ))
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">No job openings at the moment. Check back soon!</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Roles We Hire */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold">Roles We're Often Hiring</h2>
            <p className="mt-4 text-lg text-gray-400 max-w-2xl mx-auto">
              Even if you don't see your role listed above, we're always interested in talented professionals.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {[
              'Civil Engineers',
              'Construction Managers',
              'Field Technicians',
              'Project Coordinators',
              'Safety Specialists',
              'CAD/BIM Technicians',
              'Structural Engineers',
              'Geotechnical Engineers',
              'Survey Technicians',
              'Quality Control Inspectors',
            ].map((role, index) => (
              <Badge
                key={index}
                variant="outline"
                className="border-gray-700 text-gray-300 hover:border-red-500 hover:text-white px-4 py-2 text-sm transition-colors"
              >
                {role}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </Layout>
  );
};

export default CareersPage;
